import { Component, OnInit, Input, ViewChild, OnDestroy } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { trigger, state, style } from '@angular/animations';
import { Subscription } from 'rxjs';

import { User } from '@classes/licence/user';
import { LicenceService } from '@services/licence.service';
import { AuthService } from '@services/auth.service';
import { UiService } from '@services/ui.service';
import { UserService } from '@services/user.service';
import { OrganismService } from '@services/organism.service';
import { Provider } from '@classes/licence/provider';
import { Region } from '@classes/region';

@Component({
    selector: 'app-users-list',
    templateUrl: './users-list.component.html',
    styleUrls: ['./users-list.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
            state('expanded', style({ height: '*' }))
        ])
    ]
})
export class UsersListComponent implements OnInit, OnDestroy {
    @Input() usersList: User[];
    @Input() isEditAllowed: boolean;
    @ViewChild(MatSort, { static: true }) sort: MatSort;

    private selectedLicenceSub: Subscription;
    private loadingSub: Subscription;

    displayedColumns: string[] = ['username', 'email', 'role', 'provider', 'language', 'lastLogonDate'];
    dataSource: MatTableDataSource<User>;

    editedId: number;
    isCreationMode: boolean;
    isSending: boolean;
    isLoading: boolean;
    regionsList: Region[];

    constructor(
        private licenceSrv: LicenceService,
        private authSrv: AuthService,
        private uiSrv: UiService,
        private userSrv: UserService,
        private organismSrv: OrganismService
    ) { }

    ngOnInit() {
        this.initTable();
        this.initSubscriptions();
    }

    onEdit(id?: number): void {
        this.isCreationMode = !id;
        this.editedId = id;
    }

    onEditFinished(): void {
        this.editedId = null;
        this.isCreationMode = false;
    }

    onSendMail(user: User): void {
        if(user.lastLogonDate != null) {
            return;
        }

        this.isSending = true;
        this.authSrv.requestPasswordInit(user.username)
        .subscribe(() => {
            this.uiSrv.showSnackbar('users.usersList.welcomeSent.summary', true);
            this.isSending = false;
        });
    }

    onChangeState(user: User, isEnabled: boolean): void {
        if (isEnabled)
        {
            const licence = this.licenceSrv.getSelectedLicence();
            const role = licence.roles.find(r => r.code === user.role.code);
            const quota = role.userQuota;

            if (quota && quota.value >= quota.max)
            {
                this.uiSrv.showSnackbar('users.rolesList.missingQuota.summary', true);
                return;
            }
        }

        this.isLoading = true;
        this.userSrv.setStatus(user, isEnabled ? 'enable' : 'disable')
        .subscribe(() => {
            this.licenceSrv.get(this.licenceSrv.getSelectedLicence().code, false)
            .subscribe(() => {
                this.usersList.map(u => {
                    if(u.id === user.id) {
                        u.isEnabled = isEnabled;
                    }
                });
                this.isLoading = false;
            });
        });
    }

    getRegion(provider: Provider): string {
        if (provider && this.regionsList) {
            return this.regionsList.find(r => r.provider.code === provider.code)?.text.shortLabel;
        }
        return null;
    }

    private initTable(): void {
        if(this.isEditAllowed) {
            this.displayedColumns.push('actions');
        }

        this.dataSource = new MatTableDataSource(this.usersList);
        this.sortTable();
    }

    private sortTable(): void {
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'role': return item.role.text.shortLabel.toLowerCase();
                case 'language': return item.language.codeISO.toLowerCase();
                default: return item[property];
            }
        };
        this.dataSource.sort = this.sort;
    }

    private initSubscriptions(): void {
        this.selectedLicenceSub = this.licenceSrv.selectedLicenceChanged
        .subscribe(licence => {
            this.dataSource.data = licence ? licence.users : [];
            this.sortTable();
            this.onEditFinished();
        });

        this.organismSrv.getRegions(false)
        .subscribe(regions => {
            this.regionsList = regions;
        });

        this.loadingSub = this.uiSrv.loadingStateChanged
        .subscribe(loadingState => {
            if(!loadingState) {
                this.isSending = loadingState;
            }
        });
    }

    ngOnDestroy(): void {
        if(this.selectedLicenceSub) {
            this.selectedLicenceSub.unsubscribe();
        }
        if(this.loadingSub) {
            this.loadingSub.unsubscribe();
        }
    }
}
